import React, { useState, useMemo } from 'react';
import ContractDocumentView from './ContractDocumentView';
import SignaturePadModal from './SignaturePadModal';
import { 
  X, 
  PenTool, 
  Stamp, 
  RotateCcw, 
  CheckCircle2, 
  ShieldCheck, 
  FileText, 
  AlertCircle,
  Eye,
  Maximize2,
  Home,
  UserCheck,
  CreditCard
} from 'lucide-react';

const ContractSignModal = ({ contract, isOpen, onClose, onSignSuccess }) => {
  if (!isOpen || !contract) return null;

  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSignaturePadOpen, setIsSignaturePadOpen] = useState(false);
  const [savedSignature, setSavedSignature] = useState(null); // { dataUrl, type }

  // Direct Employee Input: Resident Registration Number & Residential Address
  const [residentNumber, setResidentNumber] = useState(contract.ResidentRegistrationNumber || contract.residentNumber || '');
  const [userAddress, setUserAddress] = useState(contract.UserAddress || contract.userAddress || '');

  // Auto-format resident registration number (######-#######)
  const handleResidentNumberChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '').slice(0, 13);
    if (raw.length <= 6) {
      setResidentNumber(raw);
    } else {
      setResidentNumber(`${raw.slice(0, 6)}-${raw.slice(6)}`);
    }
  };

  // Real-time contract preview reflecting employee's direct input
  const previewContract = useMemo(() => ({
    ...contract,
    ResidentRegistrationNumber: residentNumber,
    UserAddress: userAddress
  }), [contract, residentNumber, userAddress]);

  const handleSignatureConfirmed = (dataUrl, type) => {
    setSavedSignature({ dataUrl, type });
  };

  const handleConfirmSign = async () => {
    // 1. Validate Resident Number (Strict 13 digits)
    const cleanRrn = residentNumber.replace(/[^0-9]/g, '');
    if (!cleanRrn || cleanRrn.length !== 13) {
      alert('전자계약서 법적 효력 확보를 위해 본인의 주민등록번호 13자리를 정확히 입력해 주세요.');
      return;
    }

    // 2. Validate Address
    if (!userAddress.trim()) {
      alert('계약서에 명기될 본인의 주민등록상 실거주지 자택 주소를 직접 입력해 주세요.');
      return;
    }

    // 3. Validate Signature
    if (!savedSignature?.dataUrl) {
      alert('계약서 체결을 위해 [고해상도 전자서명/도장 날인] 버튼을 눌러 서명을 완료해 주세요.');
      setIsSignaturePadOpen(true);
      return;
    }

    if (isSubmitting) return;

    if (!agreed) {
      alert('계약서 내용 확인 및 전자서명 동의 체크박스를 선택해 주세요.');
      return;
    }

    try {
      setIsSubmitting(true);
      await onSignSuccess(contract.ContractID, savedSignature.dataUrl, {
        IpAddress: '121.134.88.12',
        UserAgent: navigator.userAgent,
        ResidentRegistrationNumber: residentNumber,
        UserAddress: userAddress.trim()
      });
      alert('전자계약서 서명이 성공적으로 체결되었습니다!');
      onClose();
    } catch (err) {
      console.error('Sign contract error:', err);
      alert('서명 처리 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-2 md:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white">
              <PenTool className="w-5 h-5" />
            </div>
            <div className="text-left">
              <h2 className="text-base font-bold">전자계약서 검토 및 서명</h2>
              <p className="text-xs text-slate-400">주민번호 및 자택 주소를 직접 기재하고 조항 확인 후 서명을 진행해 주세요.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Document Body */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-100/70 space-y-6">
          {/* Document Render with Real-time Preview */}
          <div className="shadow-xs rounded-2xl overflow-hidden">
            <ContractDocumentView contract={previewContract} />
          </div>

          {/* Mandatory Direct Employee Input Section */}
          <div className="bg-white border-2 border-indigo-200 rounded-2xl p-5 shadow-sm text-left space-y-4">
            <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
              <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                <UserCheck className="w-4 h-4 text-indigo-600" />
                계약 당사자 인적사항 직접 작성 (필수)
              </span>
              <span className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 font-bold px-2 py-0.5 rounded">
                직원 본인 직접 기재 필수
              </span>
            </div>

            <p className="text-[11px] text-slate-600 leading-relaxed">
              근로기준법 및 전자계약 법적 효력 확보를 위해 <strong>주민등록번호</strong>와 <strong>자택 주소</strong>는 계약 당사자 본인이 직접 기재해야 합니다. (작성 즉시 위 계약서 원본에 실시간 반영됩니다.)
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              {/* Resident Number Input */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1">
                  <CreditCard className="w-3.5 h-3.5 text-indigo-600" />
                  주민등록번호 (13자리) *
                </label>
                <input
                  type="text"
                  maxLength={14}
                  placeholder="예: 950101-1234567"
                  value={residentNumber}
                  onChange={handleResidentNumberChange}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 focus:bg-white focus:border-indigo-600 rounded-xl font-mono text-xs text-slate-900 font-bold tracking-wider outline-none"
                />
                <span className="text-[10px] text-slate-400 block">
                  * 체결 후 기본 뷰 및 인쇄 시 뒷자리는 마스킹(950101-1******) 처리되어 안전하게 보호됩니다.
                </span>
              </div>

              {/* Residential Address Input */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1">
                  <Home className="w-3.5 h-3.5 text-indigo-600" />
                  자택 주소 (실거주지) *
                </label>
                <input
                  type="text"
                  placeholder="예: 서울시 강남구 테헤란로 123, 101동 202호"
                  value={userAddress}
                  onChange={(e) => setUserAddress(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 focus:bg-white focus:border-indigo-600 rounded-xl text-xs text-slate-900 font-semibold outline-none"
                />
                <span className="text-[10px] text-slate-400 block">
                  * 주민등록등본상 거주지 주소를 동·호수까지 상세히 작성해 주세요.
                </span>
              </div>
            </div>
          </div>

          {/* Signature Selection Tool Box */}
          <div className="bg-white border-2 border-indigo-200 rounded-2xl p-5 shadow-sm text-left">
            <div className="flex items-center justify-between border-b border-slate-200 pb-2">
              <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                <PenTool className="w-4 h-4 text-indigo-600" />
                전자서명 또는 도장 날인 *
              </span>
              <span className="text-[11px] text-slate-500">
                서명자: <strong className="text-slate-900">{contract.UserName}</strong>
              </span>
            </div>

            {savedSignature ? (
              <div className="p-4 bg-white border border-indigo-200 rounded-2xl flex items-center justify-between shadow-2xs mt-4">
                <div className="flex items-center gap-3">
                  <div className="w-24 h-16 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-center p-1 overflow-hidden">
                    <img src={savedSignature.dataUrl} alt="서명 미리보기" className="max-w-full max-h-full object-contain" />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-emerald-700 flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      {savedSignature.type === 'DRAW' ? '고해상도 자필 서명 날인 완료' : '공인 전자도장 날인 완료'}
                    </span>
                    <span className="text-[11px] text-slate-500 block mt-0.5">정상적으로 전자서명 데이터가 생성되었습니다.</span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setIsSignaturePadOpen(true)}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl cursor-pointer"
                  >
                    다시 서명하기
                  </button>
                  <button
                    type="button"
                    onClick={() => setSavedSignature(null)}
                    className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-xl cursor-pointer"
                    title="서명 삭제"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-5 bg-white border-2 border-dashed border-indigo-200 rounded-2xl flex flex-col items-center justify-center text-center space-y-2.5 mt-4">
                <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 shadow-2xs">
                  <PenTool className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-900">전자서명 또는 도장 날인이 필요합니다</h4>
                  <p className="text-[11px] text-slate-500">확대 패드에서 부드러운 필압 터치 서명 또는 자동 도장 생성을 지원합니다.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsSignaturePadOpen(true)}
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md cursor-pointer flex items-center gap-2 transition-transform active:scale-95"
                >
                  <Maximize2 className="w-4 h-4" />
                  <span>✍️ 고해상도 확대 서명 패드 열기</span>
                </button>
              </div>
            )}

            {/* Legal Agreement Confirmation */}
            <div className="mt-4 pt-3 border-t border-slate-200">
              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer"
                />
                <span className="text-xs text-slate-700 font-medium leading-relaxed">
                  본인은 상기 계약서의 모든 조항 및 특약 사항을 충분히 확인하였으며, 전자서명법에 따라 상기 전자서명(또는 날인)이 법적 효력을 가짐에 전적으로 동의합니다.
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-white border-t border-slate-200 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer"
          >
            취소 및 닫기
          </button>

          <button
            type="button"
            disabled={!agreed || isSubmitting}
            onClick={handleConfirmSign}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-bold text-white transition-all shadow-md ${
              agreed && !isSubmitting
                ? 'bg-indigo-600 hover:bg-indigo-700 active:scale-98 cursor-pointer'
                : 'bg-slate-300 text-slate-500 cursor-not-allowed shadow-none'
            }`}
          >
            <CheckCircle2 className="w-4 h-4" />
            {isSubmitting ? '전자서명 체결 중...' : '계약서 최종 서명 및 체결 완료'}
          </button>
        </div>

        {/* High-Resolution Expanded Signature Modal */}
        <SignaturePadModal
          isOpen={isSignaturePadOpen}
          onClose={() => setIsSignaturePadOpen(false)}
          onConfirm={handleSignatureConfirmed}
          initialName={contract.UserName || ''}
          title={`'${contract.Title}' 전자계약 서명 날인`}
        />
      </div>
    </div>
  );
};

export default ContractSignModal;
